import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSyncLogAttempts } from "@/services/channelCenter";

/**
 * GYG-LOG-1 · 同步日志一行的「展开看逐次请求 / 返回」（只读）。
 * 数据 = 这条出站事件的每一次真实 HTTP 调用：请求方法 / 地址 / 请求体、返回状态码 / 返回体、起止时间。
 * 不含任何凭据（请求头 / 密码 / 令牌不入库）；保留 30 天，超期的留痕已被清理。
 * 权限沿用渠道中心「同步日志·查看」（老板 / 技术管理员）——整个渠道中心本来就只给这两类角色。
 */
export function SyncAttemptsToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="attempts-toggle"
      className="rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
    >
      {open ? "收起请求/返回" : "请求/返回"}
    </button>
  );
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("zh-CN", { hour12: false }) : "—");
const pretty = (v: unknown) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};
const prettyText = (t: string | null) => {
  if (!t) return "（空）";
  try {
    return JSON.stringify(JSON.parse(t), null, 2);
  } catch {
    return t;
  }
};

export function SyncAttemptsRow({ eventId, colSpan }: { eventId: string; colSpan: number }) {
  const q = useQuery({ queryKey: ["channel-center", "sync-attempts", eventId], queryFn: () => getSyncLogAttempts(eventId) });
  const [showBody, setShowBody] = useState<Record<string, boolean>>({});
  return (
    <tr data-testid="attempts-row" className="bg-slate-50">
      <td colSpan={colSpan} className="px-4 py-3">
        {q.isLoading ? (
          <div className="text-[12px] text-slate-500">加载中…</div>
        ) : q.isError ? (
          <button type="button" onClick={() => q.refetch()} className="text-[12px] font-medium text-rose-600 hover:underline">加载失败，点击重试</button>
        ) : (q.data ?? []).length === 0 ? (
          <div className="text-[12px] text-slate-400">没有逐次请求记录（这条事件没有真实发出过请求，或超过 30 天已清理）</div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {q.data!.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid="attempt-item">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
                  <b className="text-slate-900">第 {a.attempt_no} 次发送{a.call_no > 1 || (q.data ?? []).some((x) => x.attempt_no === a.attempt_no && x.call_no > 1) ? ` · 请求 ${a.call_no}` : ""}</b>
                  <span className="tabular-nums">{fmt(a.started_at)} → {fmt(a.finished_at)}</span>
                  <span className={`rounded px-1.5 py-0.5 font-medium ${a.outcome === "sent" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                    {a.response_status ?? "无响应"} · {a.outcome}
                  </span>
                  <span className="font-mono text-[11.5px] text-slate-500">{a.request_method} {a.request_url}</span>
                  <button type="button" onClick={() => setShowBody((s) => ({ ...s, [a.id]: !s[a.id] }))} className="text-blue-600 hover:underline">
                    {showBody[a.id] ? "收起内容" : "看请求体 / 返回体"}
                  </button>
                </div>
                {showBody[a.id] && (
                  <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
                    <div>
                      <div className="mb-1 text-[11px] font-semibold text-slate-500">请求体</div>
                      <pre className="max-h-64 overflow-auto rounded bg-slate-50 p-2 text-[11.5px] leading-snug text-slate-700" data-testid="attempt-request">{a.request_body == null ? "（无请求体）" : pretty(a.request_body)}</pre>
                    </div>
                    <div>
                      <div className="mb-1 text-[11px] font-semibold text-slate-500">返回体（最多 8KB）</div>
                      <pre className="max-h-64 overflow-auto rounded bg-slate-50 p-2 text-[11.5px] leading-snug text-slate-700" data-testid="attempt-response">{prettyText(a.response_body)}</pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
